import { ObjectUtil } from "./ObjectUtil";
import { StringUtil } from "./StringUtil";

export class UrlUtil {
    public static isAbsoluteUrl(path: string) {
        path = path.toLowerCase();
        return path.startsWith("http://") || path.startsWith("https://");
    }

    /**
     * This function accepts either a query string or an array of strings or objects and returns a query string that can be passed on a URL.
     * Example usages with the return value:
     *
     * buildQueryString("someParameter=value")  -> "?someParameter=value"
     * buildQueryString("?someParameter=value") -> "?someParameter=value"
     * buildQueryString("someParameter=value&other=value2") -> "?someParameter=value&other=value2"
     * buildQueryString(["someParameter=value", "other=value2"]) -> "?someParameter=value&other=value2"
     * buildQueryString({someParameter:"value", other:"value2"}) -> "?someParameter=value&other=value2"
     *
     * @param {*} queryStringOrArray
     */
    public static buildQueryString(queryStringOrArrayOrObject: string | string[] | any) {
        if (queryStringOrArrayOrObject == null)
            return "";
        let params: URLSearchParams;
        if (typeof queryStringOrArrayOrObject === "string") {
            params = new URLSearchParams(queryStringOrArrayOrObject);
        } else if (Array.isArray(queryStringOrArrayOrObject)) {
            params = new URLSearchParams(queryStringOrArrayOrObject.join("&"))
        } else if (ObjectUtil.isObject(queryStringOrArrayOrObject)) {
            params = new URLSearchParams();

            for (const [key, value] of Object.entries(queryStringOrArrayOrObject)) {
                if (value != null) {
                    const stringVal = (typeof value === "object") ? JSON.stringify(value) : String(value);
                    if (!StringUtil.isEmptyString(stringVal))
                        params.set(key, stringVal);
                }
            }
        }
        const queryString = params?.toString() ?? "";
        return !StringUtil.isEmptyString(queryString) ? `?${queryString}` : "";
    }

    public static trimQueryString(url: string): string {
        if (url != null) {
            const index = url.indexOf("?");
            if (index >= 0)
                return url.substring(0, index);
        }
        return url;
    }

    public static getPropsFromUrl(url: string): any {
        const params = StringUtil.stringAfter(url, "?", false, null);
        if (params == null)
            return null;
        const urlParams = new URLSearchParams(params);
        const result = {};
        urlParams.forEach((value, key) => result[key] = value);
        return result;
    }

    public static removePropsFromUrl(url: string, propsToRemove: string[]): string {
        const questionPos = url.indexOf("?");
        if (questionPos < 0)
            return url;
        const beforeProps = url.substring(0, questionPos);
        const props = this.getPropsFromUrl(url);
        for (const propToRemove of propsToRemove) {
            delete props[propToRemove];
        }
        const newQueryString = this.buildQueryString(props);
        return beforeProps + newQueryString;
    }

    public static getSearchParamFromUrl(paramName: string, url: string = window.location.search): any {
        const searchParams = new URLSearchParams(url);
        if (searchParams == null)
            return null;
        return searchParams.get(paramName);
    }

    /**
     * This method joins a host and a path, making sure that there is only one slash between them.
     * @param host The host, e.g. "https://www.mcleodsoftware.com/" or "https://www.mcleodsoftware.com"
     * @param path The path, e.g. "/some/path" or "some/path"
     * @param params The optional query string parameters in multiple formats, with or without the leading "?"
     *  e.g. "someParameter=value&other=value2"
     *      or "?someParameter=value&other=value2"
     *      or ["someParameter=value", "other=value2"]
     *      or {someParameter:"value", other:"value2"}
     * @returns   The joined host and path,
     *      e.g. "https://www.mcleodsoftware.com/some/path?someParameter=value&other=value2"
     */
    public static joinUrl(host: string, path: string, params?: string | string[] | any) {
        let result = "";

        if (!StringUtil.isEmptyString(host)) {
            result += host;
            if (!result.endsWith("/") && !StringUtil.isEmptyString(path)) {
                result += "/";
            }
        }

        if (!StringUtil.isEmptyString(path)) {
            if (result.endsWith("/") && path.startsWith("/")) {
                result += path.substring(1);
            } else {
                result += path;
            }
        }

        if (params != null)
            result += UrlUtil.buildQueryString(params);
        return result;
    }
}
